import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Sparkles, TrendingUp, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import { Card, CardContent } from "../ui/card";

// Tier specs — copy of backend/core.py SUBSCRIPTION_TIERS, only the
// fields the UI needs to show "what you get" + upgrade arrows.
const TIER_INFO = {
  free: {
    label: "Free Tier",
    color: "gray",
    headline: "You're on the free tier",
    perks: [
      "20-credit signup bonus",
      "Browse, like, and comment",
      "Watch unlimited videos",
    ],
    upgradeTo: "beginner",
    upgradeCta: "Become a Patron — start your broadcasting journey",
    icon: Sparkles,
  },
  beginner: {
    label: "Beginner Patron",
    color: "cyan",
    headline: "You're currently on the BEGINNER plan",
    perks: [
      "$6/quarter — 80 credits delivered per quarter",
      "Record + upload videos up to 6 minutes",
      "2 sponsor links on your public profile",
    ],
    upgradeTo: "experimenter",
    upgradeCta: "Upgrade to EXPERIMENTER — 200 cr/qtr and more reach",
    icon: TrendingUp,
  },
  experimenter: {
    label: "Experimenter",
    color: "violet",
    headline: "You're currently on the EXPERIMENTER plan",
    perks: [
      "$12/quarter — 200 credits delivered per quarter",
      "Videos up to 6 minutes + channel customization",
      "Priority placement on the map",
    ],
    upgradeTo: "producer",
    upgradeCta: "Upgrade to PRODUCER — 9-min videos, 4 sponsor links, +400 cr/qtr",
    icon: Zap,
  },
  producer: {
    label: "Producer",
    color: "amber",
    headline: "You're a PRODUCER",
    perks: [
      "$24/quarter — 600 credits delivered per quarter",
      "9-minute videos (everyone else caps at 6)",
      "4 sponsor links on your profile (vs. 2 elsewhere)",
      "Own multi-channel broadcasting network + commission earnings",
    ],
    upgradeTo: null,
    upgradeCta: null,
    icon: Crown,
  },
};

const PALETTE = {
  gray:   { border: "border-gray-700",     glow: "from-gray-700/20",    chip: "bg-gray-700 text-gray-200",    cta: "bg-emerald-600 hover:bg-emerald-500" },
  cyan:   { border: "border-cyan-500/60",  glow: "from-cyan-500/20",    chip: "bg-cyan-500 text-gray-900",   cta: "bg-cyan-600 hover:bg-cyan-500" },
  violet: { border: "border-violet-500/60",glow: "from-violet-500/20",  chip: "bg-violet-500 text-white",    cta: "bg-violet-600 hover:bg-violet-500" },
  amber:  { border: "border-amber-500/60", glow: "from-amber-500/20",   chip: "bg-amber-400 text-gray-900",  cta: "bg-amber-600 hover:bg-amber-500" },
};

/**
 * SubscriptionTierCard — sits at the top of the dashboard. Shows the
 * user's current subscription tier in plain English and offers the
 * obvious upgrade path. For Producer (top tier) we instead surface a
 * "Manage subscription" link to the Stripe customer portal.
 *
 * Reads tier directly from `user.subscription_tier` (string, lowercase,
 * matches backend SUBSCRIPTION_TIERS names). Defaults to "free" when
 * the field is missing.
 */
const SubscriptionTierCard = ({ user }) => {
  const [openingPortal, setOpeningPortal] = useState(false);
  if (!user) return null;
  const tierKey = (user.subscription_tier || "free").toLowerCase();
  const info = TIER_INFO[tierKey] || TIER_INFO.free;
  const palette = PALETTE[info.color] || PALETTE.gray;
  const Icon = info.icon;

  // Open the Stripe customer portal in a new tab so users can manage /
  // cancel their subscription without leaving the dashboard context.
  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await api.post("/payments/portal", {});
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Couldn't open the Stripe portal — try again in a moment");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't open the Stripe portal");
    }
    setOpeningPortal(false);
  };

  return (
    <Card
      className={`bg-gray-900 ${palette.border} relative overflow-hidden mb-6`}
      data-testid="subscription-tier-card"
    >
      <div className={`absolute inset-0 bg-gradient-to-r ${palette.glow} to-transparent opacity-50 pointer-events-none`} />
      <CardContent className="p-5 sm:p-6 relative">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Icon className="h-5 w-5 text-white" />
              <h2 className="text-lg sm:text-xl font-bold text-white" data-testid="tier-headline">
                {info.headline}
              </h2>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${palette.chip}`}>
                {info.label}
              </span>
            </div>
            <ul className="mt-3 space-y-1 text-sm text-gray-300">
              {info.perks.map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">·</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col gap-2 items-stretch sm:items-end shrink-0">
            {info.upgradeTo ? (
              <Link
                to="/pricing"
                data-testid="tier-upgrade-cta"
                className={`${palette.cta} text-white font-semibold px-4 py-2 rounded-lg text-sm transition-colors text-center`}
              >
                {info.upgradeCta} →
              </Link>
            ) : (
              <button
                type="button"
                onClick={openPortal}
                disabled={openingPortal}
                data-testid="tier-manage-cta"
                className="border border-gray-700 hover:bg-gray-800 text-gray-300 px-4 py-2 rounded-lg text-sm transition-colors text-center disabled:opacity-50"
              >
                {openingPortal ? "Opening…" : "Manage subscription"}
              </button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionTierCard;
